import React from 'react';


export type DecimalConversionIconSizeToken = number | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_MAP: Record<string, number> = {
  sm: 16,
  md: 20,
  lg: 24,
  xl: 40,
};

const DEFAULT_SIZE = 20;
const DEFAULT_COLOR = 'currentColor';

export interface DecimalConversionIconProps {
  size?: DecimalConversionIconSizeToken;
  color?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Ícono de diseño (punto decimal + "00" + flecha) que marca un campo en formato decimal
 * como convertible a su equivalente en horas. No forma parte del set de IconScout
 * (@iconscout/react-unicons) por ser específico de este diseño (Figma node 438:12135 /
 * 438:12153, archivo "Ausentismos-Administracion").
 */
export const DecimalConversionIcon: React.FC<DecimalConversionIconProps> = ({
  size = DEFAULT_SIZE,
  color = DEFAULT_COLOR,
  className,
  'data-testid': dataTestId = 'icon-decimal-conversion',
}) => {
  const resolvedSize = typeof size === 'string' ? (SIZE_MAP[size] ?? DEFAULT_SIZE) : size;

  return (
    <svg
      width={resolvedSize}
      height={resolvedSize}
      viewBox="0 0 25 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-testid={dataTestId}
    >
      <circle cx="5.499" cy="9.5" r="1.5" fill={color} />
      <path
        fill={color}
        d="M14.499 14C13.9057 14 13.3824 13.84 12.929 13.52C12.4824 13.2 12.1324 12.75 11.879 12.17C11.6257 11.59 11.499 10.9167 11.499 10.15C11.499 9.37667 11.6224 8.70333 11.869 8.13C12.1224 7.55667 12.4724 7.11 12.919 6.79C13.3724 6.47 13.8957 6.31 14.489 6.31C15.0957 6.31 15.6224 6.47 16.069 6.79C16.5224 7.11 16.8724 7.56 17.119 8.14C17.3724 8.71333 17.499 9.38333 17.499 10.15C17.499 10.9167 17.3724 11.59 17.119 12.17C16.8724 12.7433 16.5224 13.1933 16.069 13.52C15.6224 13.84 15.099 14 14.499 14ZM14.509 12.6C14.8157 12.6 15.0757 12.5033 15.289 12.31C15.5024 12.1167 15.6657 11.8367 15.779 11.47C15.8924 11.1033 15.949 10.6633 15.949 10.15C15.949 9.63667 15.8924 9.2 15.779 8.84C15.6657 8.47333 15.499 8.19333 15.279 8C15.0657 7.80667 14.8024 7.71 14.489 7.71C14.1824 7.71 13.9224 7.80667 13.709 8C13.4957 8.19333 13.3324 8.47333 13.219 8.84C13.1057 9.2 13.049 9.63667 13.049 10.15C13.049 10.6567 13.1057 11.0933 13.219 11.46C13.3324 11.8267 13.4957 12.11 13.709 12.31C13.929 12.5033 14.1957 12.6 14.509 12.6Z"
      />
      <path
        fill={color}
        d="M21.501 14C20.9076 14 20.3843 13.84 19.931 13.52C19.4843 13.2 19.1343 12.75 18.881 12.17C18.6276 11.59 18.501 10.9167 18.501 10.15C18.501 9.37667 18.6243 8.70333 18.871 8.13C19.1243 7.55667 19.4743 7.11 19.921 6.79C20.3743 6.47 20.8976 6.31 21.491 6.31C22.0976 6.31 22.6243 6.47 23.071 6.79C23.5243 7.11 23.8743 7.56 24.121 8.14C24.3743 8.71333 24.501 9.38333 24.501 10.15C24.501 10.9167 24.3743 11.59 24.121 12.17C23.8743 12.7433 23.5243 13.1933 23.071 13.52C22.6243 13.84 22.101 14 21.501 14ZM21.511 12.6C21.8176 12.6 22.0776 12.5033 22.291 12.31C22.5043 12.1167 22.6676 11.8367 22.781 11.47C22.8943 11.1033 22.951 10.6633 22.951 10.15C22.951 9.63667 22.8943 9.2 22.781 8.84C22.6676 8.47333 22.501 8.19333 22.281 8C22.0676 7.80667 21.8043 7.71 21.491 7.71C21.1843 7.71 20.9243 7.80667 20.711 8C20.4976 8.19333 20.3343 8.47333 20.221 8.84C20.1076 9.2 20.051 9.63667 20.051 10.15C20.051 10.6567 20.1076 11.0933 20.221 11.46C20.3343 11.8267 20.4976 12.11 20.711 12.31C20.931 12.5033 21.1976 12.6 21.511 12.6Z"
      />
      <path
        fill={color}
        transform="translate(15, 13)"
        d="M3.1449 0.00715385C3.0441 0.0252573 2.95057 0.0717889 2.87534 0.141268C2.8001 0.210747 2.74628 0.30028 2.72023 0.39932C2.68936 0.538529 2.71806 0.707529 2.7901 0.806654C2.82369 0.853237 3.25215 1.29632 3.74181 1.79086L4.63285 2.69057L2.51385 2.69761L0.394313 2.70411L0.278397 2.76803C0.177359 2.82249 0.0967425 2.90829 0.0486799 3.01253C0.000617205 3.11676 -0.0122914 3.23378 0.0118967 3.34599C0.0416884 3.47545 0.165188 3.65528 0.25673 3.7024C0.380772 3.76686 0.693313 3.77607 2.65685 3.77661H4.63773L3.7716 4.64869C3.29494 5.12807 2.8616 5.57874 2.80852 5.64915C2.7159 5.7732 2.71265 5.78294 2.71373 5.93082C2.71427 6.11661 2.76356 6.22386 2.90548 6.34844C3.05931 6.48386 3.22885 6.5169 3.42494 6.44974C3.51485 6.4194 3.66652 6.27749 4.64585 5.30682C5.13659 4.82291 5.62213 4.33376 6.1024 3.83945C6.45773 3.46245 6.49348 3.40611 6.49348 3.22032C6.49348 3.01665 6.47019 2.98957 5.02231 1.54495C4.27644 0.800154 3.61994 0.153404 3.56415 0.107904C3.4531 0.0174457 3.31281 -0.0161378 3.1449 0.00715385Z"
      />
    </svg>
  );
};

export default DecimalConversionIcon;
